import { GuidedTourTooltip } from '../../../components/GuidedTour/GuidedTourTooltip.tsx';
import { selectAppliedBathsAdjDisplay } from '../../../redux/features/selectors/adjustmentSelectors/selectBathsAdj.ts';
import { selectAppliedConditionAdjDisplay } from '../../../redux/features/selectors/adjustmentSelectors/selectConditionAdj.ts';
import { selectAppliedExternalObsolescenceAdjDisplay } from '../../../redux/features/selectors/adjustmentSelectors/selectExternalObsolescenceAdj.ts';
import { selectAppliedFunctionalUtilityAdjDisplay } from '../../../redux/features/selectors/adjustmentSelectors/selectFunctionalUtilityAdj.ts';
import { selectAppliedManufacturedGLAAdjDisplay } from '../../../redux/features/selectors/adjustmentSelectors/selectManufacturedGLAAdj.ts';
import { selectAppliedMultiFamilyGBAAdjDisplay } from '../../../redux/features/selectors/adjustmentSelectors/selectMultiFamilyGBAdj.ts';
import { selectAppliedMultiFamilyUnitsAdjDisplay } from '../../../redux/features/selectors/adjustmentSelectors/selectMultiFamilyUnitsAdj.ts';
import { selectAppliedQualityAdjDisplay } from '../../../redux/features/selectors/adjustmentSelectors/selectQualityAdj.ts';
import { selectAppliedSFRGLAAdjDisplay } from '../../../redux/features/selectors/adjustmentSelectors/selectSFRGLAAdj.ts';
import { selectBathsCostDisplay } from '../../../redux/features/selectors/costSelectors/selectBathsCost.ts';
import { selectFoundationCostDisplay } from '../../../redux/features/selectors/costSelectors/selectFoundationCost.ts';
import { selectManufacturedGlaAndFixedCostDisplay } from '../../../redux/features/selectors/costSelectors/selectManfacturedCost.ts';
import {
  selectMultiFamilyGBACostDisplay,
  selectMultiFamilyUnitsCostDisplay,
} from '../../../redux/features/selectors/costSelectors/selectMultiFamilyCost.ts';
import { selectSFRCostDisplay } from '../../../redux/features/selectors/costSelectors/selectSFRCost.ts';
import {
  selectDepreciationDisplay,
  selectExternalDepreciationDisplay,
  selectExternalDepreciationPercentageDisplay,
  selectFunctionalDepreciationDisplay,
  selectFunctionalDepreciationPercentageDisplay,
  selectPhysicalDepreciationDisplay,
} from '../../../redux/features/selectors/depreciationSelectors.ts';
import { selectEffectiveYear } from '../../../redux/features/selectors/orderSelectors.ts';
import {
  selectImprovementsById,
  selectImprovementsDisplay,
  selectQualityDisplay,
  selectSubjectIsManufacturedHome,
} from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectCostNewDisplay } from '../../../redux/features/selectors/selectCostNew.ts';
import { selectFirstOrFewerCompID } from '../../../redux/features/selectors/selectFirstOrFewerCompID.ts';
import { selectImprovementsCostDisplay } from '../../../redux/features/selectors/selectImprovementsCost.ts';
import {
  selectImprovementsValueRatioDisplay,
  selectSubjectImprovementsValueRatioDisplay,
} from '../../../redux/features/selectors/selectImprovementsValueRatio.ts';
import {
  selectOrderHasExternalDepreciation,
  selectOrderHasFunctionalDepreciation,
} from '../../../redux/features/selectors/selectOrderDepreciation.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { RootState } from '../../../redux/types.ts';
import { ContentBlock } from '../ColumnComponents/ContentBlock.tsx';
import { Divider } from '../ColumnComponents/Divider.tsx';
import { ModeAwareDetailLine } from '../ColumnComponents/ModeAwareDetailLine.tsx';
import { TotalLine } from '../ColumnComponents/TotalLine.tsx';
import { PLACEHOLDER } from '../consts.ts';
import { ViewMode } from '../types.ts';
import { FeatureDetails } from './Improvements/FeatureDetails.tsx';
import { useDepreciatedCostCalculationString } from './utils/useDepreciatedCostCalculationString.ts';

type ImprovementsProps = {
  id: string;
  viewMode: ViewMode;
};

const Gla = ({ id, viewMode }: ImprovementsProps) => {
  const { gla } = useAppSelector((state: RootState) => selectImprovementsDisplay(state, id));
  const SFRCost = useAppSelector((state: RootState) => selectSFRCostDisplay(state, id));
  const SFRAdj = useAppSelector((state: RootState) => selectAppliedSFRGLAAdjDisplay(state, id));

  return (
    <ModeAwareDetailLine
      text={gla}
      costValue={SFRCost}
      adj={SFRAdj}
      viewMode={viewMode}
      topBorder
    />
  );
};

const Gba = ({ id, viewMode }: ImprovementsProps) => {
  const gba = useAppSelector((state: RootState) => selectImprovementsDisplay(state, id)).gla;
  const multiFamilyCost = useAppSelector((state: RootState) =>
    selectMultiFamilyGBACostDisplay(state, id),
  );
  const multiFamilyAdj = useAppSelector((state: RootState) =>
    selectAppliedMultiFamilyGBAAdjDisplay(state, id),
  );

  return (
    <ModeAwareDetailLine
      text={gba}
      costValue={multiFamilyCost}
      adj={multiFamilyAdj}
      viewMode={viewMode}
    />
  );
};

const ManufacturedGla = ({ id, viewMode }: ImprovementsProps) => {
  const { gla } = useAppSelector((state: RootState) => selectImprovementsDisplay(state, id));
  const manufacturedCost = useAppSelector((state: RootState) =>
    selectManufacturedGlaAndFixedCostDisplay(state, id),
  );
  const manufacturedAdj = useAppSelector((state: RootState) =>
    selectAppliedManufacturedGLAAdjDisplay(state, id),
  );

  return (
    <ModeAwareDetailLine
      text={gla}
      costValue={manufacturedCost}
      adj={manufacturedAdj}
      viewMode={viewMode}
      topBorder
    />
  );
};

const Foundation = ({ id, viewMode }: ImprovementsProps) => {
  const { foundation } = useAppSelector((state: RootState) => selectImprovementsDisplay(state, id));
  const foundationCost = useAppSelector((state: RootState) =>
    selectFoundationCostDisplay(state, id),
  );

  return <ModeAwareDetailLine text={foundation} costValue={foundationCost} viewMode={viewMode} />;
};

const Placeholder = ({ viewMode }: Pick<ImprovementsProps, 'viewMode'>) => (
  <ModeAwareDetailLine viewMode={viewMode} topBorder />
);

const Units = ({ id, viewMode }: ImprovementsProps) => {
  const units = useAppSelector((state: RootState) =>
    selectImprovementsDisplay(state, id),
  ).numberOfUnits;
  const cost = useAppSelector((state: RootState) => selectMultiFamilyUnitsCostDisplay(state, id));
  const adj = useAppSelector((state: RootState) =>
    selectAppliedMultiFamilyUnitsAdjDisplay(state, id),
  );

  return <ModeAwareDetailLine text={units} costValue={cost} adj={adj} viewMode={viewMode} />;
};

const Baths = ({ id, viewMode }: ImprovementsProps) => {
  const { baths } = useAppSelector((state: RootState) => selectImprovementsDisplay(state, id));
  const bathsCost = useAppSelector((state: RootState) => selectBathsCostDisplay(state, id));
  const bathsAdj = useAppSelector((state: RootState) => selectAppliedBathsAdjDisplay(state, id));

  return (
    <ModeAwareDetailLine
      text={baths}
      costValue={bathsCost}
      adj={bathsAdj}
      viewMode={viewMode}
      topBorder
    />
  );
};

const ActualAge = ({ id, viewMode }: ImprovementsProps) => {
  const improvements = useAppSelector((state: RootState) => selectImprovementsDisplay(state, id));
  const effectiveYear = useAppSelector((state: RootState) => selectEffectiveYear(state));

  const age = improvements?.age;
  const yearBuilt = age && effectiveYear ? parseInt(effectiveYear) - parseInt(age) : undefined;

  const text =
    age && yearBuilt
      ? `Actual Age ${age} ${age === '1' ? 'yr' : 'yrs'} (${yearBuilt})`
      : `Actual Age ${PLACEHOLDER}`;

  return <ModeAwareDetailLine text={text} viewMode={viewMode} topBorder />;
};

const Condition = ({ id, viewMode }: ImprovementsProps) => {
  const improvements = useAppSelector((state: RootState) => selectImprovementsDisplay(state, id));
  const effectiveAgeCost = useAppSelector((state: RootState) =>
    selectPhysicalDepreciationDisplay(state, id),
  );
  const effectiveAgeAdj = useAppSelector((state: RootState) =>
    selectAppliedConditionAdjDisplay(state, id),
  );

  const text = `Condition ${improvements?.condition ?? PLACEHOLDER}/E${
    improvements?.effectiveAge ?? PLACEHOLDER
  }`;

  return (
    <ModeAwareDetailLine
      text={text}
      costValue={effectiveAgeCost}
      adj={effectiveAgeAdj}
      viewMode={viewMode}
      topBorder
    />
  );
};

const Quality = ({ id, viewMode }: ImprovementsProps) => {
  const quality = useAppSelector((state: RootState) => selectQualityDisplay(state, id));
  const qualityAdj = useAppSelector((state: RootState) =>
    selectAppliedQualityAdjDisplay(state, id),
  );

  return <ModeAwareDetailLine text={quality} adj={qualityAdj} viewMode={viewMode} />;
};

const FunctionalDepreciation = ({ id, viewMode }: ImprovementsProps) => {
  const orderHasFunctionalDep = useAppSelector(selectOrderHasFunctionalDepreciation);
  const funcDepPercent = useAppSelector((state: RootState) =>
    selectFunctionalDepreciationPercentageDisplay(state, id),
  );
  const funcDepAdj = useAppSelector((state: RootState) =>
    selectAppliedFunctionalUtilityAdjDisplay(state, id),
  );
  const funcDepValue = useAppSelector((state: RootState) =>
    selectFunctionalDepreciationDisplay(state, id),
  );

  if (!orderHasFunctionalDep) {
    return null;
  }

  const text = viewMode === 'adjustments' ? 'Functional Utility' : funcDepPercent;

  return (
    <ModeAwareDetailLine
      text={text ?? PLACEHOLDER}
      adj={funcDepAdj}
      costValue={funcDepValue}
      viewMode={viewMode}
      topBorder
    />
  );
};

const ExternalDepreciation = ({ id, viewMode }: ImprovementsProps) => {
  const orderHasExternalDep = useAppSelector(selectOrderHasExternalDepreciation);
  const extDepPercent = useAppSelector((state: RootState) =>
    selectExternalDepreciationPercentageDisplay(state, id),
  );
  const extDepAdj = useAppSelector((state: RootState) =>
    selectAppliedExternalObsolescenceAdjDisplay(state, id),
  );
  const extDepValue = useAppSelector((state: RootState) =>
    selectExternalDepreciationDisplay(state, id),
  );

  if (!orderHasExternalDep) {
    return null;
  }

  const text = viewMode === 'adjustments' ? 'External Obsolescence' : extDepPercent;

  return (
    <ModeAwareDetailLine
      text={text ?? PLACEHOLDER}
      adj={extDepAdj}
      costValue={extDepValue}
      viewMode={viewMode}
      topBorder
    />
  );
};

const MultiFamilyMainStructure = ({ id, viewMode }: ImprovementsProps) => {
  const MultiFamilyCost = useAppSelector((state: RootState) => selectCostNewDisplay(state, id));
  const subjectIsManufactured = useAppSelector(selectSubjectIsManufacturedHome);
  return (
    <ContentBlock>
      <Quality id={id} viewMode={viewMode} />
      {subjectIsManufactured && <Placeholder viewMode={viewMode} />}
      <Gba id={id} viewMode={viewMode} />
      <Units id={id} viewMode={viewMode} />
      <Baths id={id} viewMode={viewMode} />
      <FeatureDetails id={id} viewMode={viewMode} />
      {viewMode !== 'adjustments' ? (
        <TotalLine label="Cost New" value={MultiFamilyCost ?? PLACEHOLDER} subTotal />
      ) : (
        <>
          <Condition id={id} viewMode={viewMode} />
          <FunctionalDepreciation id={id} viewMode={viewMode} />
          <ExternalDepreciation id={id} viewMode={viewMode} />
        </>
      )}
    </ContentBlock>
  );
};

const SFRMainStructure = ({ id, viewMode }: ImprovementsProps) => {
  const SFRCost = useAppSelector((state: RootState) => selectCostNewDisplay(state, id));
  const subjectIsManufactured = useAppSelector(selectSubjectIsManufacturedHome);

  return (
    <ContentBlock>
      <Quality id={id} viewMode={viewMode} />
      {subjectIsManufactured && <Placeholder viewMode={viewMode} />}
      <Gla id={id} viewMode={viewMode} />
      <Baths id={id} viewMode={viewMode} />
      <FeatureDetails id={id} viewMode={viewMode} />
      {viewMode !== 'adjustments' ? (
        <TotalLine label="Cost New" value={SFRCost ?? PLACEHOLDER} subTotal />
      ) : (
        <>
          <Condition id={id} viewMode={viewMode} />
          <FunctionalDepreciation id={id} viewMode={viewMode} />
          <ExternalDepreciation id={id} viewMode={viewMode} />
        </>
      )}
    </ContentBlock>
  );
};

const ManufacturedMainStructure = ({ id, viewMode }: ImprovementsProps) => {
  const manufacturedCost = useAppSelector((state: RootState) => selectCostNewDisplay(state, id));
  return (
    <ContentBlock>
      <Quality id={id} viewMode={viewMode} />
      <Foundation id={id} viewMode={viewMode} />
      <ManufacturedGla id={id} viewMode={viewMode} />
      <Baths id={id} viewMode={viewMode} />
      <FeatureDetails id={id} viewMode={viewMode} />
      {viewMode !== 'adjustments' ? (
        <TotalLine label="Cost New" value={manufacturedCost ?? PLACEHOLDER} subTotal />
      ) : (
        <>
          <Condition id={id} viewMode={viewMode} />
          <FunctionalDepreciation id={id} viewMode={viewMode} />
          <ExternalDepreciation id={id} viewMode={viewMode} />
        </>
      )}
    </ContentBlock>
  );
};

const MainStructure = ({ id, viewMode }: { id: string; viewMode: ViewMode }): JSX.Element => {
  const improvements = useAppSelector((state) => selectImprovementsById(state, id));

  switch (improvements?.propertyType) {
    case 'multiFamily':
      return <MultiFamilyMainStructure id={id} viewMode={viewMode} />;
    case 'manufacturedHome':
      return <ManufacturedMainStructure id={id} viewMode={viewMode} />;
    default:
      return <SFRMainStructure id={id} viewMode={viewMode} />;
  }
};

type TotalLineParams = { id: string; improvementsCost: string | undefined };

const SubjectTotalLine = ({ id, improvementsCost }: TotalLineParams) => {
  const improvementsValueRatio = useAppSelector((state: RootState) =>
    selectSubjectImprovementsValueRatioDisplay(state, id),
  );
  const depreciatedCostCalculation = useDepreciatedCostCalculationString(id);

  return (
    <TotalLine
      label="Improvements"
      value={improvementsCost ?? PLACEHOLDER}
      extra={improvementsValueRatio ?? PLACEHOLDER}
      tooltipContent={[
        depreciatedCostCalculation,
        'Cost New - Depreciation = Depreciated Cost of Improvements',
      ]}
    />
  );
};

const CompTotalLine = ({ id, improvementsCost }: TotalLineParams) => {
  const improvementsValueRatio = useAppSelector((state: RootState) =>
    selectImprovementsValueRatioDisplay(state, id),
  );
  const depreciatedCostCalculation = useDepreciatedCostCalculationString(id);

  return (
    <TotalLine
      label="Improvements"
      value={improvementsCost ?? PLACEHOLDER}
      extra={improvementsValueRatio ?? PLACEHOLDER}
      tooltipContent={[
        depreciatedCostCalculation,
        'Cost New - Depreciation = Depreciated Cost of Improvements',
      ]}
    />
  );
};

export const Improvements = ({ id, viewMode }: ImprovementsProps) => {
  const depreciation = useAppSelector((state: RootState) => selectDepreciationDisplay(state, id));
  const improvementsCost = useAppSelector((state: RootState) =>
    selectImprovementsCostDisplay(state, id),
  );
  const firstOrFewerCompID = useAppSelector(selectFirstOrFewerCompID);

  return (
    <>
      <GuidedTourTooltip
        text={[
          'After you’ve added comps, review property features for accuracy and completeness.',
          'Toolkit automatically fills in property features with your appraisal XML, listing and public records data.',
        ]}
        displayCondition={id === firstOrFewerCompID}
        tourID="analysisTour"
        tourIndex={1}
      >
        <MainStructure id={id} viewMode={viewMode} />
      </GuidedTourTooltip>

      {viewMode !== 'adjustments' && (
        <ContentBlock>
          <ActualAge id={id} viewMode={viewMode} />
          <Condition id={id} viewMode={viewMode} />
          <FunctionalDepreciation id={id} viewMode={viewMode} />
          <ExternalDepreciation id={id} viewMode={viewMode} />
          <TotalLine label="Total Depreciation" value={depreciation ?? PLACEHOLDER} subTotal />
        </ContentBlock>
      )}

      <Divider />
      <ContentBlock>
        {id === 'subject' ? (
          <SubjectTotalLine id={id} improvementsCost={improvementsCost} />
        ) : (
          <CompTotalLine id={id} improvementsCost={improvementsCost} />
        )}
      </ContentBlock>
    </>
  );
};
